import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import RiderShell from "@/components/rider/app/RiderShell";
import CategoryResults from "@/components/rider/app/CategoryResults";
import RiderTrailMap from "@/components/rider/app/RiderTrailMap";
import RiderShareDialog from "@/components/rider/app/RiderShareDialog";
import GalleryLightbox from "@/components/rider/app/GalleryLightbox";
import { DriveToTrailhead } from "@/components/rider/app/DriveToTrailhead";
import { RIDER_CATEGORIES } from "@/components/rider/app/categories";
import { Loader2, ChevronLeft, MapPin, Clock, Mountain, Gauge, Route as RouteIcon, Bike, Compass, Home,
  Baby, GraduationCap, Zap, Dog, Accessibility, Sparkles, Landmark, Leaf, ShieldAlert } from "lucide-react";

const ACCESS = [
  { key: "family_friendly", label: "Family friendly", icon: Baby },
  { key: "beginner_friendly", label: "Beginner friendly", icon: GraduationCap },
  { key: "ebike_friendly", label: "E-bike friendly", icon: Zap },
  { key: "dog_friendly", label: "Dog friendly", icon: Dog },
];
const HIGHLIGHTS = [
  { key: "scenic_highlights", label: "Scenic highlights", icon: Sparkles },
  { key: "historical_info", label: "History", icon: Landmark },
  { key: "seasonal_info", label: "Seasonal notes", icon: Leaf },
  { key: "safety_notes", label: "Safety", icon: ShieldAlert },
];

function useIsDesktop() {
  const [d, setD] = useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const m = window.matchMedia("(min-width: 1024px)");
    const h = (e) => setD(e.matches);
    m.addEventListener("change", h);
    return () => m.removeEventListener("change", h);
  }, []);
  return d;
}

export default function RiderBikePathDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [cat, setCat] = useState(sp.get("cat") || RIDER_CATEGORIES[0].key);
  const [lightbox, setLightbox] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [drive, setDrive] = useState(null);
  const showDrive = (r) => {
    setDrive(r);
    if (r) setTimeout(() => document.querySelector("[data-testid='rider-map-section']")?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
  };
  const isDesktop = useIsDesktop();

  useEffect(() => {
    setLoading(true);
    api.get(`/rider/bike-paths/${id}/detail`)
      .then(({ data }) => setD(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const gallery = useMemo(() => d?.gallery || [], [d]);

  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      // If the pin detail dialog is open, let it handle Escape first; keep fullscreen.
      if (document.querySelector('[data-testid="rider-biz-detail"]')) return;
      setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [expanded]);

  if (loading) return <RiderShell wide><div className="h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div></RiderShell>;
  if (notFound || !d) return <RiderShell wide><div className="h-screen flex flex-col items-center justify-center gap-3 p-8 text-center"><p className="text-muted-foreground">This bike path isn't available.</p><button onClick={() => nav("/explore")} className="text-primary text-sm font-semibold">Back to explore</button></div></RiderShell>;

  const o = d.overview || {}, det = d.details || {}, acc = d.accessibility || {}, hi = d.highlights || {}, rel = d.relationships || {};
  const sb = det.surface_breakdown || {};
  const surfaceParts = [["Paved", sb.paved_pct], ["Crushed stone", sb.crushed_stone_pct], ["Gravel", sb.gravel_pct], ["Dirt", sb.dirt_pct]].filter(([, v]) => v != null && v > 0);
  const activeAccess = ACCESS.filter((a) => acc[a.key]);
  const activeHi = HIGHLIGHTS.filter((h) => (hi[h.key] || "").trim());
  const am = rel.amenities || {};
  const mapPts = [...(rel.advertisers || []), ...(rel.bike_shops || []), ...(am.bathrooms || []), ...(am.parking || []), ...(am.trailheads || []), ...(am.water_stations || [])].filter((p) => p.lat != null);
  const hasMap = (d.boundary?.length >= 2) || (d.centerline?.length >= 2) || ((d.lines || []).flat().length >= 2) || (rel.routes || []).some((r) => (r.coordinates?.length || 0) >= 2) || mapPts.length > 0;

  const Stat = ({ icon: Icon, label, value }) => value == null || value === "" ? null : (
    <div className="rounded-xl bg-card border border-border p-2.5 text-center">
      <Icon className="w-4 h-4 mx-auto text-primary" />
      <div className="font-heading font-bold text-sm mt-1 leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">{label}</div>
    </div>
  );

  // ---- Shared content sections (same markup for mobile + desktop) ----
  const descEl = o.description ? <p className="text-sm text-muted-foreground" data-testid="rider-detail-desc">{o.description}</p> : null;

  const statsEl = (
    <div className="grid grid-cols-4 gap-2">
      <Stat icon={MapPin} label="Length" value={det.length_mi != null ? `${det.length_mi} mi` : null} />
      <Stat icon={Clock} label="Ride time" value={det.estimated_ride_time} />
      <Stat icon={Gauge} label="Difficulty" value={det.difficulty} />
      <Stat icon={Mountain} label="Elevation" value={det.elevation_gain_ft != null ? `${det.elevation_gain_ft} ft` : null} />
    </div>
  );

  const galleryEl = gallery.length > 0 ? (
    <div data-testid="rider-gallery">
      <h2 className="font-heading font-bold text-sm uppercase tracking-wide text-muted-foreground mb-2">Gallery</h2>
      <div className="flex gap-3 overflow-x-auto snap-x pb-2">
        {gallery.map((g, i) => (
          <figure key={i} className="snap-start shrink-0 w-56" data-testid={`rider-gallery-img-${i}`}>
            <button onClick={() => setLightbox(i)} data-testid={`rider-gallery-open-${i}`} className="block w-full hover:opacity-90 active:scale-[0.98] transition-all">
              <img src={g.url} alt={g.caption || ""} className="w-56 h-36 object-cover rounded-xl border border-border" loading="lazy" />
            </button>
            {g.caption && <figcaption className="text-[11px] text-muted-foreground mt-1 px-0.5">{g.caption}</figcaption>}
          </figure>
        ))}
      </div>
    </div>
  ) : null;

  const mapEl = (mapHeight) => hasMap ? (
    <div data-testid="rider-map-section">
      <h2 className="font-heading font-bold text-sm uppercase tracking-wide text-muted-foreground mb-2 lg:hidden">Trail map</h2>
      <RiderTrailMap boundary={d.boundary} rel={rel} centerline={d.centerline} lines={d.lines} height={mapHeight} drive={drive} onExpand={() => setExpanded(true)} />
    </div>
  ) : null;

  const surfaceEl = (det.surface_type || surfaceParts.length > 0) ? (
    <div data-testid="rider-surface">
      <h2 className="font-heading font-bold text-sm uppercase tracking-wide text-muted-foreground mb-2">Surface</h2>
      {det.surface_type && <div className="text-sm">{det.surface_type}</div>}
      {surfaceParts.length > 0 && (
        <>
          <div className="flex h-2.5 rounded-full overflow-hidden mt-2 bg-secondary">
            {surfaceParts.map(([l, v], i) => <div key={l} style={{ width: `${v}%`, background: ["#22D3EE", "#A3A3A3", "#F59E0B", "#92400E"][i] }} />)}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-muted-foreground">
            {surfaceParts.map(([l, v], i) => <span key={l} className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: ["#22D3EE", "#A3A3A3", "#F59E0B", "#92400E"][i] }} />{l} {v}%</span>)}
          </div>
        </>
      )}
    </div>
  ) : null;

  const accessEl = (activeAccess.length > 0 || (acc.ada_notes || "").trim()) ? (
    <div data-testid="rider-accessibility">
      <h2 className="font-heading font-bold text-sm uppercase tracking-wide text-muted-foreground mb-2">Good to know</h2>
      <div className="flex flex-wrap gap-2">
        {activeAccess.map((a) => (
          <span key={a.key} className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1.5 rounded-full" data-testid={`rider-access-${a.key}`}><a.icon className="w-3.5 h-3.5" />{a.label}</span>
        ))}
      </div>
      {(acc.ada_notes || "").trim() && (
        <div className="mt-3 flex gap-2 text-sm text-muted-foreground"><Accessibility className="w-4 h-4 text-primary mt-0.5 shrink-0" /><span>{acc.ada_notes}</span></div>
      )}
    </div>
  ) : null;

  const highlightsEl = activeHi.length > 0 ? (
    <div className="space-y-4" data-testid="rider-highlights">
      <h2 className="font-heading font-bold text-sm uppercase tracking-wide text-muted-foreground">Highlights</h2>
      {activeHi.map((h) => (
        <div key={h.key} className="flex gap-3" data-testid={`rider-highlight-${h.key}`}>
          <div className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0"><h.icon className="w-4 h-4" /></div>
          <div><div className="font-heading font-bold text-sm">{h.label}</div><p className="text-sm text-muted-foreground mt-0.5">{hi[h.key]}</p></div>
        </div>
      ))}
    </div>
  ) : null;

  const routesList = (rel.routes || []).filter((r) => !r.linked_stop_id);
  const routesEl = routesList.length > 0 ? (
    <div data-testid="rider-routes">
      <h2 className="font-heading font-bold text-sm uppercase tracking-wide text-muted-foreground mb-2">Routes</h2>
      <div className="space-y-2">
        {routesList.map((r) => (
          <div key={r.id} className="flex items-center gap-2.5 rounded-xl bg-card border border-border px-3 py-2.5" data-testid={`rider-route-${r.id}`}>
            <span className="w-3 h-3 rounded-full shrink-0" style={{ background: r.color || "#22D3EE" }} />
            <span className="text-sm font-medium flex-1 min-w-0 truncate">{r.name}</span>
            <RouteIcon className="w-4 h-4 text-muted-foreground" />
          </div>
        ))}
      </div>
    </div>
  ) : null;

  const exploreEl = (
    <div data-testid="rider-explore">
      <h2 className="font-heading font-bold text-sm uppercase tracking-wide text-muted-foreground mb-3">Explore along the trail</h2>
      <div className="flex gap-2 overflow-x-auto pb-2">
        {RIDER_CATEGORIES.map((c) => (
          <button key={c.key} onClick={() => setCat(c.key)} data-testid={`rider-cat-tab-${c.key}`}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold border transition-colors ${cat === c.key ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/50"}`}>
            <c.icon className="w-3.5 h-3.5" />{c.label}
          </button>
        ))}
      </div>
      <div className="mt-3">
        <CategoryResults pathId={id} category={cat} />
      </div>
    </div>
  );

  const hero = (
    <div className="relative h-72 lg:h-80 bg-secondary">
      {o.hero_image ? <img src={o.hero_image} alt={o.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-primary/40 via-primary/10 to-secondary" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/30" />
      <button onClick={() => nav(-1)} data-testid="rider-back" className="absolute top-4 left-4 w-9 h-9 rounded-full bg-black/50 backdrop-blur flex items-center justify-center text-white hover:bg-black/70 active:scale-95 transition-all"><ChevronLeft className="w-5 h-5" /></button>
      <RiderShareDialog url={`${window.location.origin}/rider/bike-path/${id}`} title={o.name} />
      <div className="absolute bottom-4 left-4 right-4 lg:left-8 lg:right-8">
        <h1 className="font-heading font-extrabold text-white text-2xl lg:text-4xl leading-tight drop-shadow" data-testid="rider-detail-name">{o.name}</h1>
      </div>
    </div>
  );

  return (
    <RiderShell wide>
      {/* Desktop-only slim top bar */}
      <header className="flex sticky top-0 z-40 items-center gap-2 px-4 lg:px-8 h-14 bg-background/85 backdrop-blur border-b border-border">
        <a href="https://velopaths.com" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="nav-logo-home">
          <Bike className="w-5 h-5 text-primary" />
          <span className="font-heading font-bold tracking-tight">VeloPaths</span>
        </a>
        <a href="https://velopaths.com" className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors" data-testid="nav-home-link"><Home className="w-4 h-4" />Home</a>
        <button onClick={() => nav("/explore")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors" data-testid="rider-explore-link">
          <Compass className="w-4 h-4" /><span className="hidden sm:inline">Explore all paths</span>
        </button>
      </header>

      <div className="pb-12">
        {hero}

        {isDesktop ? (
          /* ---------- DESKTOP: map left (sticky), content right ---------- */
          <div className="grid grid-cols-12 gap-6 px-8 mt-6" data-testid="rider-desktop-layout">
            <div className="col-span-7 xl:col-span-8">
              <div className="sticky top-20">
                {mapEl("calc(100vh - 7rem)")}
              </div>
            </div>
            <div className="col-span-5 xl:col-span-4 space-y-6 pb-6">
              {descEl}
              {statsEl}
              <DriveToTrailhead pathId={id} route={drive} onRoute={showDrive} />
              {galleryEl}
              {surfaceEl}
              {accessEl}
              {highlightsEl}
              {routesEl}
              {exploreEl}
            </div>
          </div>
        ) : (
          /* ---------- MOBILE: single column (unchanged order) ---------- */
          <div className="px-4 mt-4 space-y-6">
            {descEl}
            {statsEl}
            <DriveToTrailhead pathId={id} route={drive} onRoute={showDrive} />
            {galleryEl}
            {mapEl(320)}
            {surfaceEl}
            {accessEl}
            {highlightsEl}
            {routesEl}
            {exploreEl}
          </div>
        )}
      </div>
      <GalleryLightbox images={gallery} index={lightbox} onClose={() => setLightbox(null)} />
      {expanded && (
        <div className="fixed inset-0 z-[60] bg-background flex flex-col" data-testid="rider-map-fullscreen">
          <div className="flex items-center gap-3 px-4 h-14 border-b border-border shrink-0" style={{ paddingTop: "env(safe-area-inset-top)" }}>
            <button onClick={() => setExpanded(false)} data-testid="rider-map-close" aria-label="Close map"
              className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center hover:opacity-80 active:scale-95 transition-all">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="font-heading font-bold truncate" data-testid="rider-map-fullscreen-title">{o.name}</span>
          </div>
          <div className="flex-1 min-h-0">
            <RiderTrailMap boundary={d.boundary} rel={rel} centerline={d.centerline} lines={d.lines} height="100%" fullscreen drive={drive} />
          </div>
        </div>
      )}
    </RiderShell>
  );
}
